/**
 * DevChatListPreview - 채팅 목록 마스터-디테일 목업 (DEV ONLY)
 *
 * 방향 확정용 정적 목업. 실제 supabase/실시간 채팅에 연결하지 않고
 * 더미 데이터로 "왼쪽 방목록 | 가운데 인라인 대화 | 오른쪽 구매자·견적 패널"
 * 3단 상담 인박스(채널톡/인터콤식) 레이아웃을 눈으로 확인하기 위한 화면이다.
 *
 * 사용법:
 *   /dev/chat-preview
 *
 * ⚠️ 공유 컴포넌트(ChatWindow.tsx)는 전혀 건드리지 않는다.
 *    왼쪽 목록은 실제 ChatListItem(표시 전용)을 재사용해 톤을 맞추고,
 *    가운데 대화 + 오른쪽 패널은 이 파일 안에서 정적으로 흉내낸다.
 *
 * ⚠️ App.tsx에서 import.meta.env.DEV 일 때만 라우트가 붙으므로
 *    프로덕션 빌드에는 포함되지 않는다. (실서비스 영향 0)
 */

import { useEffect, useState } from "react";
import {
  cn,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@semo/shared-ui";
import { formatChatTime } from "@semo/shared-utils";
import { PartyAvatar } from "@semo/shared-chat";
import {
  Info,
  Send,
  Paperclip,
  Phone,
  Mail,
  User,
  FileText,
  ShieldCheck,
  PanelRightClose,
  PanelRightOpen,
} from "@semo/icons";

// ────────────────────────────────────────────────────────────
// 더미 데이터
// ────────────────────────────────────────────────────────────

interface QuoteItem {
  name: string;
  price: number;
}

interface Quote {
  version: number;
  amount: number;
  items: QuoteItem[];
  time: string;
}

interface MockMessage {
  id: string;
  from: "me" | "them"; // me = 판매자(공급사), them = 구매자
  kind?: "text" | "quote";
  text?: string;
  quote?: Quote;
  time: string;
}

// 실제 플로우: 참여 → 견적 즉시 발송 → (구매자 읽음) 대화·협의 → 체결
// 견적은 항상 발송된 상태이므로 "견적 대기"는 없음.
type DealStatus =
  | "awaiting" // 응답 대기 (견적 발송, 구매자 미응답)
  | "negotiating" // 협의중 (구매자가 읽고 대화 시작)
  | "contracted"; // 계약 체결

const STATUS_CONFIG: Record<
  DealStatus,
  { label: string; badge: string; dot: string }
> = {
  awaiting: {
    label: "응답 대기",
    badge: "bg-amber-50 text-amber-700 border-amber-200",
    dot: "bg-amber-500",
  },
  negotiating: {
    label: "협의중",
    badge: "bg-violet-50 text-violet-700 border-violet-200",
    dot: "bg-violet-500",
  },
  contracted: {
    label: "계약 체결",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200",
    dot: "bg-emerald-500",
  },
};

interface MockRoom {
  id: string;
  partyName: string;
  projectName: string;
  projectCode: string;
  status: DealStatus;
  lastMessage: string;
  timestamp: string;
  unreadCount: number;
  buyer: {
    displayName: string; // 계약 전에는 마스킹된 형태
    manager: string;
    phone: string;
    email: string;
    isNew: boolean;
    contactRevealed: boolean; // 계약 체결 시 true
  };
  latestQuote?: Quote;
  messages: MockMessage[];
}

const won = (n: number) => `${n.toLocaleString("ko-KR")}원`;

const MOCK_ROOMS: MockRoom[] = [
  {
    id: "r1",
    partyName: "구매자#10DD80",
    projectName: "카페 인테리어",
    projectCode: "REQ-2026-0142",
    status: "negotiating",
    lastMessage: "네 견적서 확인했습니다. 단가만 조금 조정 가능할까요?",
    timestamp: "2026-07-02T09:52:00.000Z",
    unreadCount: 2,
    buyer: {
      displayName: "구매자#10DD80",
      manager: "홍**",
      phone: "010-****-5678",
      email: "h****@brewinglab.com",
      isNew: true,
      contactRevealed: false,
    },
    latestQuote: {
      version: 1,
      amount: 11000,
      items: [{ name: "견적테스트", price: 10000 }],
      time: "오후 4:26",
    },
    messages: [
      { id: "m1", from: "them", text: "안녕하세요, 카페 인테리어 견적 문의드립니다.", time: "오전 9:30" },
      { id: "m2", from: "me", text: "안녕하세요! 문의 감사합니다. 평수와 희망 일정 알려주시면 견적 도와드릴게요.", time: "오전 9:34" },
      { id: "m3", from: "them", text: "약 20평이고, 다음 달 초 오픈 목표입니다.", time: "오전 9:40" },
      {
        id: "m4",
        from: "me",
        kind: "quote",
        quote: { version: 1, amount: 11000, items: [{ name: "견적테스트", price: 10000 }], time: "오후 4:26" },
        time: "오후 4:26",
      },
      { id: "m5", from: "them", text: "네 견적서 확인했습니다. 단가만 조금 조정 가능할까요?", time: "오전 9:52" },
    ],
  },
  {
    id: "r2",
    partyName: "구매자#22AF10",
    projectName: "사무실 집기",
    projectCode: "REQ-2026-0138",
    status: "awaiting",
    lastMessage: "견적서를 보냈습니다. 확인 부탁드립니다.",
    timestamp: "2026-07-02T08:20:00.000Z",
    unreadCount: 0,
    buyer: {
      displayName: "구매자#22AF10",
      manager: "김**",
      phone: "010-****-3333",
      email: "k****@technova.co.kr",
      isNew: false,
      contactRevealed: false,
    },
    latestQuote: {
      version: 2,
      amount: 3400000,
      items: [
        { name: "사무용 책상 (10EA)", price: 1800000 },
        { name: "사무용 의자 (10EA)", price: 1200000 },
        { name: "수납장 (4EA)", price: 400000 },
      ],
      time: "오전 8:20",
    },
    messages: [
      { id: "m1", from: "them", text: "사무실 집기 일괄 견적 가능한가요?", time: "오전 8:05" },
      { id: "m2", from: "me", text: "네 가능합니다. 품목 리스트 주시면 정리해서 견적 드리겠습니다.", time: "오전 8:12" },
      {
        id: "m3",
        from: "me",
        kind: "quote",
        quote: {
          version: 2,
          amount: 3400000,
          items: [
            { name: "사무용 책상 (10EA)", price: 1800000 },
            { name: "사무용 의자 (10EA)", price: 1200000 },
            { name: "수납장 (4EA)", price: 400000 },
          ],
          time: "오전 8:20",
        },
        time: "오전 8:20",
      },
    ],
  },
  {
    id: "r3",
    partyName: "이영희 대표",
    projectName: "매장 간판",
    projectCode: "REQ-2026-0131",
    status: "contracted",
    lastMessage: "감사합니다! 계약 진행할게요.",
    timestamp: "2026-07-01T17:10:00.000Z",
    unreadCount: 0,
    buyer: {
      displayName: "영희네 베이커리",
      manager: "이영희 대표",
      phone: "010-4444-5555",
      email: "younghee@ybakery.com",
      isNew: false,
      contactRevealed: true, // 계약 체결 → 연락처 공개
    },
    latestQuote: {
      version: 1,
      amount: 1500000,
      items: [{ name: "외부 채널 간판 (LED)", price: 1500000 }],
      time: "어제 오후 4:40",
    },
    messages: [
      { id: "m1", from: "them", text: "간판 시안 잘 받았습니다.", time: "어제 오후 4:50" },
      { id: "m2", from: "me", text: "마음에 드셨다니 다행입니다 😊", time: "어제 오후 5:02" },
      { id: "m3", from: "them", text: "감사합니다! 계약 진행할게요.", time: "어제 오후 5:10" },
    ],
  },
  {
    id: "r4",
    partyName: "구매자#31C0A2",
    projectName: "주방 설비",
    projectCode: "REQ-2026-0125",
    status: "awaiting",
    lastMessage: "견적서를 보냈습니다. 확인 부탁드립니다.",
    timestamp: "2026-06-30T14:00:00.000Z",
    unreadCount: 0,
    buyer: {
      displayName: "구매자#31C0A2",
      manager: "박**",
      phone: "010-****-7777",
      email: "p****@minsufood.com",
      isNew: true,
      contactRevealed: false,
    },
    latestQuote: {
      version: 1,
      amount: 5200000,
      items: [
        { name: "업소용 냉장고", price: 2200000 },
        { name: "가스레인지 (2구)", price: 1500000 },
        { name: "스테인리스 작업대", price: 1500000 },
      ],
      time: "6월 30일",
    },
    messages: [
      {
        id: "m1",
        from: "me",
        kind: "quote",
        quote: {
          version: 1,
          amount: 5200000,
          items: [
            { name: "업소용 냉장고", price: 2200000 },
            { name: "가스레인지 (2구)", price: 1500000 },
            { name: "스테인리스 작업대", price: 1500000 },
          ],
          time: "6월 30일",
        },
        time: "6월 30일",
      },
      { id: "m2", from: "me", text: "견적서를 보냈습니다. 확인 부탁드립니다.", time: "6월 30일" },
    ],
  },
];

// ────────────────────────────────────────────────────────────
// 견적서 카드 (대화용 / 패널용 공용)
// ────────────────────────────────────────────────────────────

function QuoteCard({ quote, compact }: { quote: Quote; compact?: boolean }) {
  return (
    <div
      className={cn(
        "rounded-2xl border border-slate-200 bg-white overflow-hidden",
        compact ? "w-full" : "w-[300px]",
      )}
    >
      <div className="flex items-center justify-between px-4 pt-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary-text flex items-center justify-center">
            <FileText className="w-5 h-5" />
          </div>
          <span className="text-base font-semibold text-slate-900">
            견적서 v{quote.version}
          </span>
        </div>
        <span className="text-xs text-slate-400">{quote.time}</span>
      </div>
      <div className="px-4 py-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-500">제안 금액</span>
          <span className="text-lg font-semibold text-primary-text">
            {won(quote.amount)}
          </span>
        </div>
        <div className="mt-3">
          <p className="text-xs text-slate-400 mb-1.5">
            견적 항목 ({quote.items.length})
          </p>
          <div className="space-y-1">
            {quote.items.map((it, i) => (
              <div
                key={i}
                className="flex items-center justify-between text-sm"
              >
                <span className="text-slate-700 truncate pr-2">{it.name}</span>
                <span className="text-slate-500 shrink-0">{won(it.price)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex gap-2 px-4 pb-4">
        <button className="flex-1 h-9 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50">
          견적서 확인
        </button>
        <button className="flex-1 h-9 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary-hover">
          견적서 수정
        </button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// 가운데: 정적 대화 패널
// ────────────────────────────────────────────────────────────

function MockConversation({
  room,
  showInfo,
  onToggleInfo,
}: {
  room: MockRoom;
  showInfo: boolean;
  onToggleInfo: () => void;
}) {
  return (
    <div className="flex-1 flex flex-col min-w-0 bg-white">
      {/* 헤더 */}
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary-hover text-white flex items-center justify-center font-semibold shrink-0">
            {room.partyName.charAt(0)}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-slate-900 truncate">
                {room.partyName}
              </h3>
              <span
                className={cn(
                  "shrink-0 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-2xs font-medium border",
                  STATUS_CONFIG[room.status].badge,
                )}
              >
                <span
                  className={cn(
                    "w-1.5 h-1.5 rounded-full",
                    STATUS_CONFIG[room.status].dot,
                  )}
                />
                {STATUS_CONFIG[room.status].label}
              </span>
              <span className="shrink-0 px-2 py-0.5 rounded-md text-2xs font-medium bg-slate-100 border border-slate-200 text-slate-600">
                {room.projectName} · {room.projectCode}
              </span>
            </div>
          </div>
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={onToggleInfo}
                className={cn(
                  "shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-colors",
                  showInfo
                    ? "bg-primary/10 text-primary-text"
                    : "text-slate-400 hover:bg-slate-100 hover:text-slate-600",
                )}
              >
                {showInfo ? (
                  <PanelRightClose className="w-5 h-5" />
                ) : (
                  <PanelRightOpen className="w-5 h-5" />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {showInfo ? "구매자 정보 접기" : "구매자 정보 펼치기"}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* 메시지 영역 */}
      <div className="flex-1 bg-slate-50/50 overflow-y-auto px-5 py-6 space-y-3">
        <div className="flex items-center justify-center my-2">
          <span className="text-2xs text-slate-400 bg-slate-100 rounded-full px-3 py-1">
            2026년 7월 2일
          </span>
        </div>
        {room.messages.map((msg) => (
          <div
            key={msg.id}
            className={cn(
              "flex",
              msg.from === "me" ? "justify-end" : "justify-start",
            )}
          >
            <div
              className={cn(
                "max-w-[75%] flex flex-col gap-1",
                msg.from === "me" ? "items-end" : "items-start",
              )}
            >
              {msg.kind === "quote" && msg.quote ? (
                <QuoteCard quote={msg.quote} />
              ) : (
                <div
                  className={cn(
                    "px-4 py-2.5 rounded-2xl text-sm leading-relaxed",
                    msg.from === "me"
                      ? "bg-primary text-primary-foreground rounded-br-md"
                      : "bg-white border border-slate-200 text-slate-800 rounded-bl-md",
                  )}
                >
                  {msg.text}
                </div>
              )}
              <span className="text-2xs text-slate-400 px-1">{msg.time}</span>
            </div>
          </div>
        ))}
      </div>

      {/* 입력창 (정적) */}
      <div className="border-t border-slate-100 px-4 py-3 shrink-0">
        <div className="flex items-center gap-2 bg-slate-50 rounded-2xl px-3 py-2">
          <button className="text-slate-400 hover:text-slate-600 shrink-0">
            <Paperclip className="w-5 h-5" />
          </button>
          <input
            disabled
            placeholder="메시지를 입력하세요 (목업 — 입력 불가)"
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400"
          />
          <button className="w-9 h-9 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shrink-0 opacity-60">
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// 오른쪽: 구매자 정보 + 최신 견적 패널
// ────────────────────────────────────────────────────────────

function BuyerInfoPanel({ room }: { room: MockRoom }) {
  const { buyer } = room;
  return (
    <div className="hidden lg:flex w-[340px] border-l border-slate-100 bg-white flex-col overflow-y-auto shrink-0">
      {/* 구매자 정보 */}
      <div className="p-5 border-b border-slate-100">
        <p className="text-xs text-slate-400 mb-3">구매자 정보</p>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary to-primary text-white flex items-center justify-center text-lg font-semibold shrink-0">
            {buyer.displayName.charAt(0)}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-base font-semibold text-slate-900 truncate">
                {buyer.displayName}
              </p>
            </div>
            {buyer.isNew && (
              <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-md text-2xs font-medium bg-emerald-50 text-emerald-600 border border-emerald-100">
                <ShieldCheck className="w-3 h-3" /> 신규 회원
              </span>
            )}
          </div>
        </div>

        <div className="space-y-3">
          <InfoRow
            icon={<User className="w-4 h-4" />}
            label="담당자"
            value={buyer.manager}
            masked={!buyer.contactRevealed}
            maskWidth="w-16"
          />
          <InfoRow
            icon={<Phone className="w-4 h-4" />}
            label="연락처"
            value={buyer.phone}
            masked={!buyer.contactRevealed}
            maskWidth="w-28"
          />
          <InfoRow
            icon={<Mail className="w-4 h-4" />}
            label="이메일"
            value={buyer.email}
            masked={!buyer.contactRevealed}
            maskWidth="w-36"
          />
        </div>

        {!buyer.contactRevealed && (
          <div className="mt-4 flex items-start gap-2 rounded-xl bg-slate-50 border border-slate-100 px-3 py-2.5">
            <Info className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
            <p className="text-xs text-slate-500 leading-relaxed">
              계약이 체결되면 상세 연락처가 공개됩니다.
            </p>
          </div>
        )}
      </div>

      {/* 최신 견적 (협의 핵심 → 우선 노출) */}
      <div className="p-5 border-b border-slate-100">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <span className="w-1 h-4 rounded-full bg-primary inline-block" />
            최신 견적
          </p>
          {room.latestQuote && (
            <span className="px-2 py-0.5 rounded-md text-2xs font-medium bg-slate-100 text-slate-500 border border-slate-200">
              v{room.latestQuote.version}
            </span>
          )}
        </div>
        {room.latestQuote ? (
          <QuoteCard quote={room.latestQuote} compact />
        ) : (
          <div className="rounded-xl border border-dashed border-slate-200 py-8 text-center">
            <p className="text-sm text-slate-400">아직 전송한 견적이 없습니다.</p>
            <button className="mt-2 text-sm font-medium text-primary-text hover:underline">
              견적서 작성
            </button>
          </div>
        )}
      </div>

      {/* 프로젝트 정보 (참고용 → 하단) */}
      <div className="p-5">
        <p className="text-sm font-semibold text-slate-900 flex items-center gap-2 mb-3">
          <span className="w-1 h-4 rounded-full bg-primary inline-block" />
          프로젝트 정보
        </p>
        <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500">프로젝트</span>
            <span className="text-sm font-medium text-slate-800 truncate max-w-[180px]">
              {room.projectName}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500">요청번호</span>
            <span className="text-sm text-slate-700">{room.projectCode}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500">진행 상태</span>
            <span
              className={cn(
                "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-2xs font-medium border",
                STATUS_CONFIG[room.status].badge,
              )}
            >
              <span
                className={cn(
                  "w-1.5 h-1.5 rounded-full",
                  STATUS_CONFIG[room.status].dot,
                )}
              />
              {STATUS_CONFIG[room.status].label}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({
  icon,
  label,
  value,
  masked = false,
  maskWidth = "w-24",
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  /** 계약 체결 전: 완전 가림(모자이크) — 유추 불가, 로딩 스켈레톤처럼 안 보이게 */
  masked?: boolean;
  maskWidth?: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-2xs text-slate-400">{label}</p>
        {masked ? (
          <span
            className={`mt-1 inline-block h-3.5 rounded-[3px] ${maskWidth}`}
            style={{
              backgroundImage:
                "repeating-linear-gradient(45deg, #cbd5e1 0, #cbd5e1 2px, #eef2f6 2px, #eef2f6 4px)",
            }}
            aria-label="계약 체결 시 공개"
            title="계약 체결 시 공개됩니다"
          />
        ) : (
          <p className="text-sm text-slate-800 truncate">{value}</p>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// 왼쪽 목록 아이템 (겹침 없는 3행 레이아웃)
//
// 공유 ChatListItem은 좁은 칸에서 프로젝트 태그가 안읽음 배지를 밀어
// 겹치는 이슈가 있어, 목업에서는 각 행이 독립 truncate 되는 전용 아이템을 쓴다.
// (실서비스 ChatListItem 수정은 Layer B에서 회귀테스트와 함께)
// ────────────────────────────────────────────────────────────

function RoomListItem({
  room,
  selected,
  onClick,
}: {
  room: MockRoom;
  selected: boolean;
  onClick: () => void;
}) {
  const isUnread = room.unreadCount > 0;
  const status = STATUS_CONFIG[room.status];
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full text-left rounded-2xl border p-4 transition-all",
        selected
          ? "bg-primary/10 border-primary/50 ring-2 ring-primary/60"
          : isUnread
            ? "bg-primary/5 border-primary/15 hover:border-primary/30"
            : "bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50/60",
      )}
    >
      <div className="flex gap-3">
        <div className="w-11 h-11 rounded-xl overflow-hidden border-2 border-slate-200 shrink-0">
          <PartyAvatar
            name={room.partyName}
            className="w-full h-full rounded-none"
            textClassName="text-base"
          />
        </div>
        <div className="flex-1 min-w-0">
          {/* 1행: 이름 + 시간 */}
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-medium text-slate-900 truncate">
              {room.partyName}
            </h3>
            <span className="text-2xs text-slate-400 shrink-0">
              {formatChatTime(room.timestamp)}
            </span>
          </div>
          {/* 2행: 상태 점 + 프로젝트 태그 */}
          <div className="flex items-center gap-1.5 mt-1 min-w-0">
            <span
              className={cn("w-1.5 h-1.5 rounded-full shrink-0", status.dot)}
            />
            <span className="text-2xs text-slate-500 truncate">
              {room.projectName} · {room.projectCode}
            </span>
          </div>
          {/* 3행: 마지막 메시지 + 안읽음 배지 */}
          <div className="flex items-center justify-between gap-2 mt-1.5">
            <p
              className={cn(
                "text-sm truncate",
                isUnread ? "text-slate-900 font-medium" : "text-slate-500",
              )}
            >
              {room.lastMessage}
            </p>
            {isUnread && (
              <span className="shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-2xs font-medium flex items-center justify-center">
                {room.unreadCount}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

// ────────────────────────────────────────────────────────────
// 메인: 마스터-디테일 3단
// ────────────────────────────────────────────────────────────

const FILTERS: { key: DealStatus | "all"; label: string }[] = [
  { key: "all", label: "전체" },
  { key: "awaiting", label: "응답 대기" },
  { key: "negotiating", label: "협의중" },
  { key: "contracted", label: "계약" },
];

// 외부(프로젝트 탭)에서 넘어온 임의 id를 목업 방에 결정적으로 매핑
// (더미라 실제 연결은 없지만, 프로젝트마다 다른 방이 선택되게 해 흐름을 재현)
function hashToRoomId(externalId: string): string {
  let h = 0;
  for (let i = 0; i < externalId.length; i++) {
    h = (h * 31 + externalId.charCodeAt(i)) | 0;
  }
  const idx = Math.abs(h) % MOCK_ROOMS.length;
  return MOCK_ROOMS[idx]!.id;
}

/**
 * ChatInboxPreview - 3단 상담 인박스 본체 (재사용 가능)
 *
 * standalone 페이지(/dev/chat-preview)와 InventoryEmbedPage 채팅 탭 preview
 * 양쪽에서 사용한다. 부모가 준 높이를 h-full로 채운다.
 *
 * targetId: 다른 탭의 "채팅하기"에서 넘어온 프로젝트 id. 넘어오면 해당 방 자동 선택.
 */
export function ChatInboxPreview({
  targetId,
}: {
  targetId?: string | null;
} = {}) {
  const firstRoom = MOCK_ROOMS[0]!;
  const [selectedId, setSelectedId] = useState<string>(
    targetId ? hashToRoomId(targetId) : firstRoom.id,
  );
  const [showInfo, setShowInfo] = useState(true); // 관리자 화면: 기본 열림
  const [statusFilter, setStatusFilter] = useState<DealStatus | "all">("all");
  const [unreadOnly, setUnreadOnly] = useState(false);

  // "채팅하기"로 다시 넘어오면(같은 탭 유지 중) 대상 방으로 전환
  useEffect(() => {
    if (targetId) setSelectedId(hashToRoomId(targetId));
  }, [targetId]);

  const filteredRooms = MOCK_ROOMS.filter((r) => {
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (unreadOnly && r.unreadCount === 0) return false;
    return true;
  });

  const selectedRoom =
    MOCK_ROOMS.find((r) => r.id === selectedId) ?? firstRoom;

  return (
    <div className="h-full flex min-h-0 gap-4">
      {/* 왼쪽: 방 목록 */}
        <div className="w-[360px] shrink-0 flex flex-col rounded-2xl bg-white border border-slate-200 overflow-hidden">
          {/* 상태 필터 */}
          <div className="px-3 pt-3 pb-2 border-b border-slate-100 shrink-0">
            <div className="flex flex-wrap gap-1.5">
              {FILTERS.map((f) => {
                const active = statusFilter === f.key;
                return (
                  <button
                    key={f.key}
                    onClick={() => setStatusFilter(f.key)}
                    className={cn(
                      "px-2.5 py-1 rounded-full text-xs font-medium border transition-colors",
                      active
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50",
                    )}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
            <label className="flex items-center gap-2 mt-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={unreadOnly}
                onChange={(e) => setUnreadOnly(e.target.checked)}
                className="w-3.5 h-3.5 rounded accent-primary"
              />
              <span className="text-xs text-slate-500">안읽음만 보기</span>
            </label>
          </div>

          {/* 목록 */}
          <div className="flex-1 overflow-y-auto p-3">
            {filteredRooms.length === 0 ? (
              <div className="py-16 text-center text-sm text-slate-400">
                조건에 맞는 채팅이 없습니다.
              </div>
            ) : (
              <div className="space-y-3">
                {filteredRooms.map((room) => (
                  <RoomListItem
                    key={room.id}
                    room={room}
                    selected={room.id === selectedId}
                    onClick={() => setSelectedId(room.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 가운데+오른쪽: 인라인 대화 + 구매자/견적 패널 */}
        <div className="flex-1 min-w-0 rounded-2xl bg-white border border-slate-200 overflow-hidden flex">
          <MockConversation
            key={selectedRoom.id}
            room={selectedRoom}
            showInfo={showInfo}
            onToggleInfo={() => setShowInfo((v) => !v)}
          />
          {showInfo && <BuyerInfoPanel room={selectedRoom} />}
        </div>
    </div>
  );
}

/**
 * DevChatListPreview - standalone 페이지 (/dev/chat-preview)
 */
export function DevChatListPreview() {
  return (
    <div className="h-screen w-full bg-slate-50 flex flex-col">
      {/* 상단 타이틀 */}
      <div className="px-6 py-4 border-b border-slate-200 bg-white shrink-0">
        <h1 className="text-lg font-semibold text-slate-900">채팅 관리</h1>
        <p className="text-sm text-slate-400 mt-0.5">
          진행 중인 대화를 한 화면에서 확인하고 바로 응대하세요 · 목업(더미 데이터)
        </p>
      </div>
      <div className="flex-1 min-h-0 p-4">
        <ChatInboxPreview />
      </div>
    </div>
  );
}
